/* eslint-disable react/prop-types */
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import Text from '../../../../shared/components/Text/Text';
import CalendarNameTooltip from './CalendarNameTooltip';
import { setActiveModal, setSelectedReduxCalendar } from '../../ducks/calendarActions';
import usePreSaveNavigationGuard from '../../hooks/usePreSaveNavigationGuard';
import useIsTextTruncated from '../../hooks/useIsTextTruncated';
import { isRestrictedCalendar } from '../../constants/restrictedCalendar';

const BreadCrumbsContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ParentBreadCrumb = styled('div')`
 cursor: pointer;
 flex-shrink: 0;
 &:hover {
    opacity: 0.7;
  }

`;

const SubBreadCrumb = styled('div')`
  cursor: ${({ isRestricted }) => (isRestricted ? 'not-allowed' : 'pointer')};
  flex: 0 1 auto;
  min-width: 0;
  max-width: 50%;
  &:hover {
    opacity: 0.7;
  }
`;

const CurrentBreadCrumb = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
`;

const BreadCrumbName = styled('span')`
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: ${(props) => (props.isParent ? 700 : 400)};
  color: ${({ theme }) => theme.colors.text};
`;

const separatorStyle = {
  transform: 'rotate(15deg)',
  paddingLeft: 5,
  paddingRight: 5,
  flexShrink: 0,
};

const BreadCrumbs = () => {
  const dispatch = useDispatch();
  const guardNavigation = usePreSaveNavigationGuard();
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars);

  // Check if the selected calendar has parent information
  const hasParentCalendar = selectedCalendar?.parentCalendarInfo
    && Object.keys(selectedCalendar.parentCalendarInfo).length > 0;
  const isParentRestricted = hasParentCalendar
    && isRestrictedCalendar(selectedCalendar.parentCalendarInfo);

  const calendarNameWriter = () => {
    if (Object.keys(selectedCalendar).length) {
      return selectedCalendar?.name ? `${selectedCalendar?.name}` : 'Select Calendar';
    }
    if (Object.keys(selectedReduxAthlete).length) {
      return selectedReduxAthlete?.first && selectedReduxAthlete?.last ? `${selectedReduxAthlete?.first} ${selectedReduxAthlete?.last}` : 'Select Calendar';
    }
    return 'Select Calendar';
  };

  const programLibraryNameWriter = () => {
    if (Object.keys(selectedReduxNonDateBasedProgram).length) {
      return selectedReduxNonDateBasedProgram?.programName ? `${selectedReduxNonDateBasedProgram?.programName}` : 'Select Program';
    }
    return 'Select Program';
  };

  const isCalendarSelected = Object.keys(selectedCalendar).length > 0
    || (Object.keys(selectedReduxAthlete).length > 0
      && selectedReduxAthlete?.first
      && selectedReduxAthlete?.last);

  const isProgramSelected = Object.keys(selectedReduxNonDateBasedProgram).length > 0;

  const currentName = programType === 'free-form-program'
    ? programLibraryNameWriter()
    : calendarNameWriter();
  const nameRef = useRef(null);
  const isNameTruncated = useIsTextTruncated(nameRef, currentName);

  const currentNameTooltipProps = isNameTruncated
    ? { 'data-tooltip-id': 'breadcrumb-name-tooltip', 'data-tooltip-content': currentName }
    : {};

  const parentName = selectedCalendar?.parentCalendarInfo?.name || '';
  const parentNameRef = useRef(null);
  const isParentNameTruncated = useIsTextTruncated(parentNameRef, parentName);

  const parentNameTooltipProps = isParentNameTruncated
    ? { 'data-tooltip-id': 'breadcrumb-name-tooltip', 'data-tooltip-content': parentName }
    : {};

  const handleParentClick = () => guardNavigation(() => {
    if (hasParentCalendar && !isParentRestricted && parentCalendars) {
      // Find the parent calendar from the array of parentCalendars
      const parentCalendar = parentCalendars.find(
        (cal) => cal.id === selectedCalendar.parentCalendarInfo.id,
      );

      if (parentCalendar) {
        dispatch(setSelectedReduxCalendar(parentCalendar));
      }
    }
  });

  const { colors } = useTheme();
  return (
    <BreadCrumbsContainer>
      <ParentBreadCrumb onClick={() => (dispatch(setActiveModal('selectCalendarModal')))}>
        {programType === 'free-form-program' ? (
          <Text
            fontWeight={700}
            fontSize='14px'
            color={colors.text}
          >
            Programs
          </Text>
        ) : (
          <Text
            fontWeight={700}
            fontSize='14px'
            color={colors.text}
          >
            Calendars
          </Text>
        )}
      </ParentBreadCrumb>

      {/* Parent Calendar Breadcrumb - Only shown for subcalendars */}
      {hasParentCalendar && programType === 'calendar' && (
        <>
          <Text style={separatorStyle} fontSize='16px' color={colors.textSecondary}> / </Text>
          <SubBreadCrumb onClick={handleParentClick} isRestricted={isParentRestricted}>
            <BreadCrumbName isParent ref={parentNameRef} {...parentNameTooltipProps}>
              {parentName}
            </BreadCrumbName>
          </SubBreadCrumb>
        </>
      )}

      {/* Current Calendar Breadcrumb */}
      {isCalendarSelected && programType === 'calendar' && (
        <>
          <Text style={separatorStyle} fontSize='16px' color={colors.textSecondary}> / </Text>
          <CurrentBreadCrumb>
            <BreadCrumbName ref={nameRef} {...currentNameTooltipProps}>
              {currentName}
            </BreadCrumbName>
          </CurrentBreadCrumb>
        </>
      )}

      {/* Current Program Breadcrumb */}
      {isProgramSelected && programType === 'free-form-program' && (
        <>
          <Text style={separatorStyle} fontSize='16px' color={colors.textSecondary}> / </Text>
          <CurrentBreadCrumb>
            <BreadCrumbName ref={nameRef} {...currentNameTooltipProps}>
              {currentName}
            </BreadCrumbName>
          </CurrentBreadCrumb>
        </>
      )}
      <CalendarNameTooltip id='breadcrumb-name-tooltip' />
    </BreadCrumbsContainer>
  );
};

export default BreadCrumbs;
